import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Spinner } from '@nextui-org/react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import HomeShowcaseEditor from '../components/HomeShowcaseEditor';
import HomePlanModalEditor from '../components/HomePlanModalEditor';

// Inicio de la app. Tres cosas se gestionan aquí:
//  · Los carruseles de «Widgets», «Animaciones LIVE» y «Gaming»: qué sale en
//    cada uno, con qué imagen y con qué texto (components/HomeShowcaseEditor).
//  · El popup «Incluido en Premium» que sale al pulsar en ellos algo que el
//    plan no incluye: sus textos (components/HomePlanModalEditor).
//  · «Síguenos en TikTok»: los vídeos de @tikcontrol.app que salen en la
//    tira. Por defecto se recogen SOLOS del perfil (los fijados y los
//    últimos, con sus visualizaciones) cada 6 h; aquí se ve su estado y se
//    puede forzar. La lista a mano es opcional: si tiene algo, sustituye a la
//    automática (se pega el enlace y las visualizaciones; la portada la trae
//    el Worker).
// Worker: cf-worker/src/api/home.js + lib/homeFeed.js + lib/homeShowcase.js.

const ENDPOINT = '/api/backoffice/home/tiktok';
const VIDEO_RE = /^https:\/\/(?:www\.)?tiktok\.com\/@[A-Za-z0-9._]{2,24}\/video\/\d{8,24}/;

const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${Math.round(v / 1e5) / 10}M`;
  if (v >= 1e3) return `${Math.round(v / 100) / 10}K`;
  return String(v);
};

const blank = () => ({ url: '', views: '', cover: '', title: '', id: '' });

export default function Inicio() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ handle: 'tikcontrol.app', max: 12 });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);   // { type: 'ok' | 'warn' | 'error', text }
  const [auto, setAuto] = useState(null);         // { videos, updatedAt, error, failedAt, everyHours }
  const [refreshing, setRefreshing] = useState(false);

  const apply = useCallback((data) => {
    setAuto(data.auto || null);
    setMeta({ handle: data.handle || 'tikcontrol.app', max: data.max || 12, updatedAt: data.updatedAt || null });
    setRows((data.videos || []).map((v) => ({ ...v, views: v.views ? String(v.views) : '' })));
  }, []);

  useEffect(() => {
    let alive = true;
    apiFetch(ENDPOINT, { cache: 'no-store' })
      .then((data) => { if (alive) apply(data); })
      .catch((e) => { if (alive) { setRows([]); setMessage({ type: 'error', text: e.message || 'No se pudo leer la lista' }); } });
    return () => { alive = false; };
  }, [apply]);

  const edit = (i, patch) => setRows((list) => list.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const move = (i, dir) => setRows((list) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => setRows((list) => list.filter((_, j) => j !== i));

  async function save() {
    const filled = rows.filter((row) => row.url.trim());
    const bad = filled.find((row) => !VIDEO_RE.test(row.url.trim()));
    if (bad) {
      setMessage({ type: 'error', text: `No es el enlace de un vídeo de TikTok: ${bad.url}` });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const data = await apiFetch(ENDPOINT, {
        method: 'PUT',
        body: { videos: filled.map((row) => ({ url: row.url.trim(), views: row.views, title: row.title, cover: row.cover })) }
      });
      apply(data);
      const notes = [];
      if (data.noCover?.length) notes.push(`${data.noCover.length} sin portada (TikTok no la ha dado; saldrán con el logo). Vuelve a guardar más tarde para reintentarlo.`);
      if (data.rejected?.length) notes.push(`${data.rejected.length} enlaces descartados.`);
      setMessage(notes.length ? { type: 'warn', text: `Guardado. ${notes.join(' ')}` } : { type: 'ok', text: filled.length ? 'Guardado. Las apps abiertas lo enseñan en un minuto, más o menos.' : 'Guardado: vuelven a salir los vídeos del perfil.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo guardar' });
    } finally {
      setBusy(false);
    }
  }

  async function refreshNow() {
    setRefreshing(true);
    setMessage(null);
    try {
      const data = await apiFetch(`${ENDPOINT}/refresh`, { method: 'POST' });
      setAuto(data.auto || null);
      if (!data.refreshed) setMessage({ type: 'warn', text: `TikTok no ha respondido (${data.auto?.error || 'sin detalle'}). Se mantiene la última lista.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo actualizar' });
    } finally {
      setRefreshing(false);
    }
  }

  const autoVideos = auto?.videos || [];
  const manual = (rows || []).some((row) => row.url.trim());
  const when = (ms) => (ms ? new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'nunca');

  const tone = { ok: 'text-success', warn: 'text-warning', error: 'text-danger' };
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Inicio de la app</h1>
        <p className="text-sm text-default-500">Lo que se gestiona a mano del Inicio. Los banners están en Popups.</p>
      </header>

      <HomeShowcaseEditor canWrite={canWrite} />

      <HomePlanModalEditor canWrite={canWrite} />

      {!rows ? <div className="p-8 flex justify-center"><Spinner /></div> : (
      <>
      <section className="max-w-4xl rounded-xl border border-default-200 bg-content1 p-5 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">Síguenos en TikTok · @{meta.handle}</h2>
            <p className="text-sm text-default-500">
              Los vídeos se recogen solos del perfil (los fijados y los últimos, con sus visualizaciones) cada {auto?.everyHours || 6} horas.
              Última vez: {when(auto?.updatedAt)}. {manual ? 'Ahora mismo NO salen en la app: manda la lista a mano de abajo.' : 'Son los que salen en la app.'}
            </p>
            {auto?.error ? <p className="text-sm text-warning">El último intento falló ({auto.error}); se mantiene la lista anterior y se reintenta en media hora.</p> : null}
          </div>
          {canWrite ? <Button size="sm" variant="flat" isLoading={refreshing} onPress={refreshNow}>Actualizar ahora</Button> : null}
        </div>
        {autoVideos.length ? (
          <ul className="flex gap-2 overflow-x-auto pb-1">
            {autoVideos.map((v) => (
              <li key={v.id} className="relative h-24 w-[68px] shrink-0 overflow-hidden rounded-md bg-default-100" title={v.title}>
                {v.cover ? <img src={v.cover} alt="" className="h-full w-full object-cover" /> : null}
                <span className="absolute inset-x-0 bottom-0 bg-black/70 px-1 text-[11px] font-semibold text-white">{compact(v.views)}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-default-500">Aún no hay vídeos recogidos.</p>}
      </section>

      <section className="max-w-4xl rounded-xl border border-default-200 bg-content1 p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Lista a mano (opcional)</h2>
          <p className="text-sm text-default-500">
            Solo si quieres elegir tú los vídeos: lo que pongas aquí sustituye a la lista automática. Pega el enlace de cada vídeo y sus
            visualizaciones (vale «552.1K» o el número); la portada se trae sola al guardar. Hasta {meta.max}, en este orden.
            Déjala vacía y guarda para volver a la automática.
          </p>
        </div>

        <ol className="space-y-2">
          {rows.map((row, i) => (
            <li key={i} className="flex items-center gap-3 rounded-lg border border-default-200 p-2">
              <div className="h-16 w-14 shrink-0 overflow-hidden rounded-md bg-default-100 grid place-items-center text-xs text-default-400">
                {row.cover ? <img src={row.cover} alt="" className="h-full w-full object-cover" /> : 'sin portada'}
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <Input
                  size="sm" aria-label={`Enlace del vídeo ${i + 1}`} placeholder={`https://www.tiktok.com/@${meta.handle}/video/…`}
                  value={row.url} isReadOnly={!canWrite} onValueChange={(url) => edit(i, { url, ...(url.trim() !== row.url.trim() ? { cover: '', title: '' } : {}) })}
                />
                {row.title ? <p className="truncate text-xs text-default-500">{row.title}</p> : null}
              </div>
              <Input
                size="sm" className="w-32 shrink-0" aria-label={`Visualizaciones del vídeo ${i + 1}`} placeholder="552.1K"
                value={row.views} isReadOnly={!canWrite} onValueChange={(views) => edit(i, { views })}
                description={/^\d+$/.test(row.views) && Number(row.views) >= 1000 ? compact(row.views) : undefined}
              />
              {canWrite ? (
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="light" isIconOnly aria-label="Subir" isDisabled={i === 0} onPress={() => move(i, -1)}>↑</Button>
                  <Button size="sm" variant="light" isIconOnly aria-label="Bajar" isDisabled={i === rows.length - 1} onPress={() => move(i, 1)}>↓</Button>
                  <Button size="sm" variant="light" color="danger" isIconOnly aria-label="Quitar" onPress={() => remove(i)}>✕</Button>
                </div>
              ) : null}
            </li>
          ))}
          {!rows.length ? <li className="text-sm text-default-500">Vacía: salen los vídeos del perfil.</li> : null}
        </ol>

        {canWrite ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="flat" isDisabled={rows.length >= meta.max} onPress={() => setRows((list) => [...list, blank()])}>Añadir vídeo</Button>
            <Button size="sm" color="primary" isLoading={busy} onPress={save}>Guardar</Button>
            {message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : null}
          </div>
        ) : (message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : null)}
      </section>

      </>
      )}
    </div>
  );
}
